import React, { useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Link, TextField, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

interface TwoFactorVerifyProps {
    challengeId: string;
    onSuccess: () => void;
    onCancel: () => void;
}

const TwoFactorVerify: React.FC<TwoFactorVerifyProps> = ({ challengeId, onSuccess, onCancel }) => {
    const { completeTwoFactorLogin } = useAuth();
    const [code, setCode] = useState('');
    const [useBackupCode, setUseBackupCode] = useState(false);
    const [rememberDevice, setRememberDevice] = useState(true);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const result = await completeTwoFactorLogin(challengeId, code, rememberDevice);
            if (result.success) {
                onSuccess();
            } else {
                setError(result.error || 'Invalid or expired code');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="auth.twoFactorVerify.page">
            <Typography variant="h5" fontWeight={600} mb={2}>
                Two-Factor Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                {useBackupCode
                    ? 'Enter one of your unused backup codes.'
                    : 'Enter the 6-digit code from your authenticator app.'}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
                <TextField
                    label={useBackupCode ? 'Backup Code' : 'Authentication Code'}
                    data-help="auth.twoFactorVerify.field.code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    sx={{ mb: 2 }}
                    autoFocus
                />
                <FormControlLabel
                    data-help="auth.twoFactorVerify.field.remember"
                    control={<Checkbox checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />}
                    label="Remember this device for 30 days"
                    sx={{ mb: 2 }}
                />
                <Button type="submit" data-help="auth.twoFactorVerify.submit" variant="contained" fullWidth disabled={isLoading} sx={{ mb: 2 }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
                </Button>
            </form>

            <Box textAlign="center">
                <Link component="button" variant="body2" data-help="auth.twoFactorVerify.switchMode" onClick={() => { setUseBackupCode(!useBackupCode); setCode(''); setError(''); }}>
                    {useBackupCode ? 'Use an authentication code instead' : 'Use a backup code instead'}
                </Link>
            </Box>
            <Box textAlign="center" sx={{ mt: 1 }}>
                <Link component="button" variant="body2" data-help="auth.twoFactorVerify.back" onClick={onCancel}>
                    Back to login
                </Link>
            </Box>
        </Box>
    );
};

export default TwoFactorVerify;
